/**
 * Profile Service
 * 
 * Handles all API calls related to user profile management
 * Communicates with Supabase backend
 */

import { supabase } from '@/services/supabase/client';
import {
  Profile,
  ProfileUpdatePayload,
  UserSession,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  ActivityLogEntry,
  ApiKey,
  ApiKeyCreatePayload,
  ApiKeyCreateResponse,
  PaymentMethod,
  SubscriptionHistoryEntry,
  TwoFactorSetup,
  TwoFactorVerifyPayload,
  PasswordChangePayload,
  DataExportRequest,
  DataExportResponse,
  AccountDeletionPayload,
} from '../types';
import { AVATAR_UPLOAD_CONFIG } from '../constants';

/**
 * Profile Service Class
 */
class ProfileService {
  /**
   * Get current user's profile
   */
  async getProfile(): Promise<Profile> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    // maybeSingle: a missing row is reported by the explicit throw below, not
    // by a 406 PGRST116 from .single(). Callers still get a rejection.
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Profile not found');

    return data as Profile;
  }

  /**
   * Get profile by user ID (for viewing other users' profiles)
   */
  async getProfileById(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle(); // no row -> null (was a 406 PGRST116 mapped to null)

    if (error) throw error;
    return data as Profile | null;
  }

  /**
   * Update profile information
   */
  async updateProfile(payload: ProfileUpdatePayload): Promise<Profile> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Failed to update profile');

    // Log activity
    await this.logActivity('profile_updated', 'profile', user.id);

    return data as Profile;
  }

  /**
   * Upload avatar image to Supabase Storage
   */
  async uploadAvatar(file: File): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Validate file size
    if (file.size > AVATAR_UPLOAD_CONFIG.maxSize) {
      throw new Error(`File size must be less than ${AVATAR_UPLOAD_CONFIG.maxSize / 1024 / 1024}MB`);
    }

    // Validate file type
    if (!AVATAR_UPLOAD_CONFIG.acceptedTypes.includes(file.type)) {
      throw new Error(`File type must be one of: ${AVATAR_UPLOAD_CONFIG.acceptedTypes.join(', ')}`);
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    const filePath = `${AVATAR_UPLOAD_CONFIG.bucketName}/${fileName}`;

    // Upload to storage
    const { data, error } = await supabase.storage
      .from(AVATAR_UPLOAD_CONFIG.bucketName)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(AVATAR_UPLOAD_CONFIG.bucketName)
      .getPublicUrl(fileName);

    // Update profile with new avatar URL
    await this.updateProfile({
      avatar_url: publicUrl,
      avatar_storage_path: fileName,
    });

    // Log activity
    await this.logActivity('avatar_uploaded', 'profile', user.id);

    return publicUrl;
  }

  /**
   * Delete avatar image
   */
  async deleteAvatar(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Get current profile to find avatar path
    const profile = await this.getProfile();
    
    if (profile.avatar_storage_path) {
      // Delete from storage
      const { error } = await supabase.storage
        .from(AVATAR_UPLOAD_CONFIG.bucketName)
        .remove([profile.avatar_storage_path]);

      if (error) throw error;
    }

    // Update profile to remove avatar
    await this.updateProfile({
      avatar_url: null,
      avatar_storage_path: null,
    });
  }

  /**
   * Change password
   */
  async changePassword(payload: PasswordChangePayload): Promise<void> {
    // Validate passwords match
    if (payload.newPassword !== payload.confirmPassword) {
      throw new Error('Passwords do not match');
    }

    // Use Supabase auth to update password
    const { error } = await supabase.auth.updateUser({
      password: payload.newPassword,
    });

    if (error) throw error;

    // Log activity
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await this.logActivity('password_changed', 'auth', user.id);
    }
  }

  /**
   * Get user sessions
   */
  async getSessions(): Promise<UserSession[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []) as UserSession[];
  }

  /**
   * Revoke a session
   */
  async revokeSession(sessionId: string, reason?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { error } = await supabase
      .from('user_sessions')
      .update({
        is_active: false,
        revoked_at: new Date().toISOString(),
        revoked_reason: reason || 'User revoked',
      })
      .eq('id', sessionId)
      .eq('user_id', user.id); // Ensure user can only revoke their own sessions

    if (error) throw error;
  }

  /**
   * Setup Two-Factor Authentication
   */
  async setup2FA(): Promise<TwoFactorSetup> {
    // This would typically call an Edge Function to generate TOTP secret
    const { data, error } = await supabase.functions.invoke('setup-2fa');

    if (error) throw error;

    return data as TwoFactorSetup;
  }

  /**
   * Verify and enable 2FA
   */
  async enable2FA(payload: TwoFactorVerifyPayload): Promise<void> {
    // This would typically call an Edge Function to verify and enable
    const { error } = await supabase.functions.invoke('enable-2fa', {
      body: payload,
    });

    if (error) throw error;

    // Update profile
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('profiles')
        .update({ two_factor_enabled: true })
        .eq('id', user.id);

      await this.logActivity('2fa_enabled', 'auth', user.id);
    }
  }

  /**
   * Disable 2FA
   */
  async disable2FA(payload: TwoFactorVerifyPayload): Promise<void> {
    // This would typically call an Edge Function to verify and disable
    const { error } = await supabase.functions.invoke('disable-2fa', {
      body: payload,
    });

    if (error) throw error;

    // Update profile
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('profiles')
        .update({ two_factor_enabled: false })
        .eq('id', user.id);

      await this.logActivity('2fa_disabled', 'auth', user.id);
    }
  }

  /**
   * Get activity log
   */
  async getActivityLog(limit: number = 50, offset: number = 0): Promise<ActivityLogEntry[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('user_activity_log')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return (data || []) as ActivityLogEntry[];
  }

  /**
   * Log user activity
   * Internal helper method
   */
  private async logActivity(
    action: string,
    resourceType?: string,
    resourceId?: string,
    details?: Record<string, any>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return; // Silently fail if not authenticated

    try {
      await supabase.from('user_activity_log').insert({
        user_id: user.id,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        details,
      });
    } catch (error) {
      // Log errors but don't throw to avoid breaking main operations
      console.error('Failed to log activity:', error);
    }
  }

  /**
   * Get notification preferences
   */
  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Notification preferences not found');

    return data as NotificationPreferences;
  }

  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(payload: NotificationPreferencesUpdate): Promise<NotificationPreferences> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .update(payload)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Failed to update notification preferences');

    return data as NotificationPreferences;
  }

  /**
   * Get API keys
   */
  async getApiKeys(): Promise<ApiKey[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []) as ApiKey[];
  }

  /**
   * Create API key
   */
  async createApiKey(payload: ApiKeyCreatePayload): Promise<ApiKeyCreateResponse> {
    // This would typically call an Edge Function to generate the key
    const { data, error } = await supabase.functions.invoke('create-api-key', {
      body: payload,
    });

    if (error) throw error;

    // Log activity
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await this.logActivity('api_key_created', 'api_key', data.apiKey.id);
    }

    return data as ApiKeyCreateResponse;
  }

  /**
   * Revoke API key
   */
  async revokeApiKey(keyId: string, reason?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { error } = await supabase
      .from('api_keys')
      .update({
        is_active: false,
        revoked_at: new Date().toISOString(),
        revoked_reason: reason || 'User revoked',
      })
      .eq('id', keyId)
      .eq('user_id', user.id);

    if (error) throw error;

    await this.logActivity('api_key_revoked', 'api_key', keyId);
  }

  /**
   * Get payment methods
   */
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .eq('user_id', user.id)
      .order('is_default', { ascending: false });

    if (error) throw error;

    return (data || []) as PaymentMethod[];
  }

  /**
   * Add payment method (Stripe)
   */
  async addPaymentMethod(stripePaymentMethodId: string): Promise<PaymentMethod> {
    // This would typically call an Edge Function to handle Stripe integration
    const { data, error } = await supabase.functions.invoke('add-payment-method', {
      body: { stripePaymentMethodId },
    });

    if (error) throw error;

    return data as PaymentMethod;
  }

  /**
   * Remove payment method
   */
  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    // This would typically call an Edge Function to remove from Stripe
    const { error } = await supabase.functions.invoke('remove-payment-method', {
      body: { paymentMethodId },
    });

    if (error) throw error;
  }

  /**
   * Get subscription history
   */
  async getSubscriptionHistory(): Promise<SubscriptionHistoryEntry[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('subscription_history')
      .select('*')
      .eq('user_id', user.id)
      .order('changed_at', { ascending: false });

    if (error) throw error;

    return (data || []) as SubscriptionHistoryEntry[];
  }

  /**
   * Update subscription
   */
  async updateSubscription(newStatus: 'free' | 'premium' | 'enterprise'): Promise<void> {
    // This would typically call an Edge Function to handle Stripe subscription
    const { error } = await supabase.functions.invoke('update-subscription', {
      body: { newStatus },
    });

    if (error) throw error;

    // Log activity
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const action = newStatus === 'free' ? 'subscription_downgraded' : 'subscription_upgraded';
      await this.logActivity(action, 'subscription', newStatus);
    }
  }

  /**
   * Request data export
   */
  async requestDataExport(request: DataExportRequest): Promise<DataExportResponse> {
    // This would typically call an Edge Function to generate export
    const { data, error } = await supabase.functions.invoke('export-user-data', {
      body: request,
    });

    if (error) throw error;

    // Log activity
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await this.logActivity('data_exported', 'export', data.exportId);
    }

    return data as DataExportResponse;
  }

  /**
   * Delete account
   */
  async deleteAccount(payload: AccountDeletionPayload): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    // This would typically call an Edge Function to handle account deletion
    const { error } = await supabase.functions.invoke('delete-account', {
      body: payload,
    });

    if (error) throw error;

    // Log activity before deletion
    await this.logActivity('account_deleted', 'account', user.id, {
      reason: payload.reason,
    });
  }
}

// Export singleton instance
export const profileService = new ProfileService();

